import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Server, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CopyBlock } from "@/components/CopyBlock";
import {
  YOU_RECEIVE,
  YOU_GENERATE,
  NEVER_COPY,
  composeInstallCommands,
  k8sCommands,
  anyRuntimeCommands,
  moveServerCommands,
  generateSecretsCommands,
} from "@/lib/deployGuide";

function ago(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function AdminDeployments() {
  const [rows, setRows] = useState(null);
  const [release, setRelease] = useState("stable");
  const [form, setForm] = useState({ label: "", customer_email: "", domain: "", license_user_id: "" });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const composeText = useMemo(() => composeInstallCommands(created || {}), [created]);
  const k8sText = useMemo(() => k8sCommands(created || {}), [created]);
  const ociText = useMemo(() => anyRuntimeCommands(created || {}), [created]);

  const load = () => {
    api.get("/admin/deployments").then((r) => setRows(r.data)).catch(() => setRows(false));
    api.get("/admin/deployments/current-release").then((r) => setRelease(r.data.version || "stable")).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label.trim() || !form.customer_email.trim()) {
      toast.error("Label and customer email are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/deployments", form);
      setCreated(data);
      setForm({ label: "", customer_email: "", domain: "", license_user_id: "" });
      load();
      toast.success("Server registered — copy the customer pack now. Secrets are shown once.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create deployment");
    } finally {
      setBusy(false);
    }
  };

  const act = async (id, action, body) => {
    try {
      await api.post(`/admin/deployments/${id}/${action}`, body || {});
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const pushAll = async () => {
    try {
      const { data } = await api.post("/admin/deployments/push-all", { version: release || "stable" });
      toast.success(`All instances set to ${data.desired_version}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Push failed");
    }
  };

  if (rows === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (rows === false) return <p className="text-sm text-destructive">Failed to load deployments.</p>;

  return (
    <div data-testid="admin-deployments">
      <PageHeader
        title="Deployments"
        subtitle="Customer runs images on any server they own. HQ never sends source or existing keys."
        action={
          <div className="flex gap-2">
            <Link to="/deploy">
              <Button variant="outline" data-testid="dep-open-guide">Customer handbook</Button>
            </Link>
            <Button className="gap-2 font-semibold" onClick={pushAll} data-testid="deploy-push-all">
              <Rocket className="h-4 w-4" /> Push {release || "stable"}
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid gap-3 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground md:grid-cols-3" data-testid="dep-install-steps">
        <div>
          <p className="font-semibold text-foreground">Customer receives</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">{YOU_RECEIVE.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        <div>
          <p className="font-semibold text-foreground">They generate</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">{YOU_GENERATE.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        <div>
          <p className="font-semibold text-foreground">Never send them</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">{NEVER_COPY.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      </div>

      <div className="mb-6 grid gap-3 rounded-2xl border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
          <Label htmlFor="dep-label">Label</Label>
          <Input id="dep-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} data-testid="dep-label" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dep-email">Customer email</Label>
          <Input id="dep-email" type="email" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))} data-testid="dep-email" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dep-domain">Domain</Label>
          <Input id="dep-domain" value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))} placeholder="gx.customer.com" data-testid="dep-domain" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dep-license-user">HQ user id (optional)</Label>
          <Input id="dep-license-user" value={form.license_user_id} onChange={(e) => setForm((f) => ({ ...f, license_user_id: e.target.value }))} placeholder="ties subscription" data-testid="dep-license-user" />
        </div>
        <div className="flex items-end">
          <Button onClick={create} disabled={busy} className="w-full font-semibold" data-testid="dep-create">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Register server"}
          </Button>
        </div>
      </div>

      {created && (
        <div className="mb-6 space-y-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5 text-sm" data-testid="dep-secrets">
          <p className="font-semibold text-foreground">Shown once — send the customer the pack for their runtime. Do not attach HQ .env.</p>
          <p className="font-mono text-xs break-all">INSTANCE_ID={created.instance_id}</p>
          <p className="font-mono text-xs break-all">INSTALL_TOKEN={created.install_token}</p>
          <p className="font-mono text-xs break-all">LICENSE_SECRET={created.license_secret}</p>
          <Tabs defaultValue="compose">
            <TabsList className="flex h-auto flex-wrap">
              <TabsTrigger value="compose">Linux / Compose</TabsTrigger>
              <TabsTrigger value="k8s">Kubernetes</TabsTrigger>
              <TabsTrigger value="oci">Any OCI</TabsTrigger>
              <TabsTrigger value="move">Move server</TabsTrigger>
            </TabsList>
            <TabsContent value="compose" className="mt-3">
              <CopyBlock text={composeText} testid="dep-install-cmd" />
            </TabsContent>
            <TabsContent value="k8s" className="mt-3">
              <CopyBlock text={k8sText} testid="dep-install-k8s" />
            </TabsContent>
            <TabsContent value="oci" className="mt-3">
              <CopyBlock text={ociText} testid="dep-install-oci" />
            </TabsContent>
            <TabsContent value="move" className="mt-3">
              <CopyBlock text={moveServerCommands()} testid="dep-install-move" />
              <p className="mt-2 text-xs text-muted-foreground">They may copy LICENSE_* and Mongo from their old box. They must generate JWT/Fernet if the database is new. Never HQ keys.</p>
            </TabsContent>
          </Tabs>
          <p className="text-xs text-muted-foreground">They still generate JWT and Fernet themselves:</p>
          <CopyBlock text={generateSecretsCommands()} testid="dep-generate-secrets" />
          <Button variant="outline" size="sm" onClick={() => setCreated(null)}>Dismiss</Button>
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <Label htmlFor="dep-release">Release tag</Label>
        <Input id="dep-release" className="max-w-xs" value={release} onChange={(e) => setRelease(e.target.value)} data-testid="dep-release" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Server</th>
              <th className="px-4 py-3">Versions</th>
              <th className="px-4 py-3">Heartbeat</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                  No customer servers yet. Register one, then send them the handbook at /deploy.
                </td>
              </tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`dep-row-${d.id}`}>
                <td className="px-4 py-3">
                  <p className="flex items-center gap-2 font-medium"><Server className="h-3.5 w-3.5 text-primary" />{d.label}</p>
                  <p className="text-xs text-muted-foreground">{d.domain || d.hostname || d.customer_email}</p>
                </td>
                <td className="px-4 py-3 text-xs">
                  <p>desired {d.desired_version || "—"}</p>
                  <p className="text-muted-foreground">reported {d.reported_version || "—"}</p>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{ago(d.last_heartbeat)}</td>
                <td className="px-4 py-3">
                  <Badge variant={d.status === "active" ? "default" : "secondary"}>{d.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => act(d.id, "push", { version: release })}>Push</Button>
                    {d.status === "restricted" || d.status === "revoked" ? (
                      <Button variant="outline" size="sm" onClick={() => act(d.id, "activate")}>Activate</Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => act(d.id, "restrict")}>Lock</Button>
                    )}
                    {d.status !== "revoked" && (
                      <Button variant="outline" size="sm" className="text-destructive" onClick={() => act(d.id, "revoke")}>Revoke</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
